const API_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Resolve a picker selection (folders / Notion databases) into the flat list of
 * files it contains, via the backend BACKGROUND job: start it, then poll for
 * the result. The listing can take minutes for a deep tree, so it runs server-
 * side off the request thread — we just poll a tiny status endpoint here.
 *
 * @param {number|string} connectionId
 * @param {Array} items - the selected picker items (may include folders)
 * @returns {Promise<Array>} resolved flat file list
 */
export async function resolveSelection(connectionId, items) {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };

  const startRes = await fetch(
    `${API_URL}data-connections/${connectionId}/resolve-selection`,
    { method: 'POST', headers, body: JSON.stringify({ items }) }
  );
  if (!startRes.ok) {
    throw new Error(`Failed to start resolve (${startRes.status})`);
  }
  const { job_id: jobId } = await startRes.json();
  if (!jobId) throw new Error('No job id returned');

  const deadline = Date.now() + 6 * 60 * 1000; // give up after 6 minutes
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 1500));

    let statusRes;
    try {
      statusRes = await fetch(
        `${API_URL}data-connections/resolve-status/${jobId}`,
        { headers }
      );
    } catch {
      continue; // transient network blip — keep polling
    }
    if (!statusRes.ok) continue;

    const data = await statusRes.json();
    if (data.status === 'done') return data.files || [];
    if (data.status === 'error') throw new Error(data.message || 'Resolve failed');
    // 'pending' / 'not_found' (job still being written) → keep polling
  }

  throw new Error('Resolve timed out');
}
